// A sheet's dismiss GESTURE, as a native scroll: the sheet is the end slot of a two-slot scroll-snap container,
// so the drag and its momentum settle run on the compositor and no React work can stutter them. Only the
// read-back is on the main thread — starving it dims the backdrop a frame late, nothing more.

import { useEffect, useLayoutEffect, type RefObject } from 'react'
// The same two-slot snap mechanic as the nav stack's back-swipe, so the thresholds come from one place.
// Relative, not aliased: this module is unit-tested and the pure-node runner resolves no `@/` alias.
import { SNAP_LANDED, observeSnapScroller, snapProgress } from '../../lib/snap-scroller'

/** How far the panel has been dragged off, `0` (at rest) → `1` (fully dismissed). */
export const dismissProgress = snapProgress
/** How far onto the empty slot a settle must land to count as dismissed. */
export const DISMISS_LANDED = SNAP_LANDED

/** How far the sheet has been dragged away, `0` → `1`. Derived from the scroll position, the compositor's truth. */

/** Nothing to do unless a settle actually landed on the empty slot: mid-flight, the browser is still snapping. */
export function settledDismissed(progress: number): boolean {
  return progress >= DISMISS_LANDED
}

/** Which way the panel is dragged away: `y` for a bottom sheet, `x` for a side one. */
export type SnapAxis = 'x' | 'y'

const scrolledOn = (el: HTMLElement, axis: SnapAxis) => (axis === 'x' ? el.scrollLeft : el.scrollTop)

const maxScrollOn = (el: HTMLElement, axis: SnapAxis) =>
  axis === 'x' ? el.scrollWidth - el.clientWidth : el.scrollHeight - el.clientHeight

function parkOn(el: HTMLElement, axis: SnapAxis, position: number): void {
  if (axis === 'x') el.scrollLeft = position
  else el.scrollTop = position
}

interface SnapDismissArgs {
  /** The scroll-snap container: `fixed inset-0`, two slots, parked on the panel. */
  scrollerRef: RefObject<HTMLElement | null>
  /** Fades with the drag. Carries `--sheet-dismiss` and `data-scrolling` (which drops its transition to 0). */
  backdropRef: RefObject<HTMLElement | null>
  axis?: SnapAxis
  /** Called once a settle lands on the empty slot. The sheet is already off screen by then. */
  onDismiss: () => void
}

export function useSnapDismiss(args: SnapDismissArgs): void {
  const { scrollerRef, backdropRef, axis = 'y', onDismiss } = args

  // Park on the panel BEFORE the first paint. A fresh scroller starts at 0 — which is the dismissed
  // slot — so anything less than a pre-paint write would flash the sheet in its gone position.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    parkOn(scroller, axis, maxScrollOn(scroller, axis))
  }, [scrollerRef, axis])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const maxScroll = () => maxScrollOn(scroller, axis)
    const progress = () => dismissProgress(scrolledOn(scroller, axis), maxScroll())
    /** Snap should have taken it home; a resize mid-gesture can leave it a pixel or two off, and a scroller
     *  parked off-rest never travels its full distance on the next drag. */
    const repark = () => {
      const rest = maxScroll()
      if (Math.abs(scrolledOn(scroller, axis) - rest) > 1) parkOn(scroller, axis, rest)
    }
    let scrolling = false

    const gesture = observeSnapScroller(scroller, {
      onStart: () => {
        scrolling = true
        // While the finger owns the sheet the backdrop must track it, not chase it through a 350ms transition.
        backdropRef.current?.setAttribute('data-scrolling', '')
      },
      onFrame: () => backdropRef.current?.style.setProperty('--sheet-dismiss', progress().toFixed(3)),
      onSettle: () => {
        scrolling = false
        backdropRef.current?.removeAttribute('data-scrolling')
        if (settledDismissed(progress())) {
          onDismiss()
          return
        }
        repark()
      },
    })

    // The panel's size changes the travel distance (a software keyboard, a row appearing after a mutation), and the
    // scroller has to stay parked on it. Never while a scroll is in flight — repositioning under a fling eats it.
    const observer = new ResizeObserver(() => {
      if (scrolling) return
      repark()
    })
    observer.observe(scroller)
    for (const child of scroller.children) observer.observe(child)

    return () => {
      gesture.stop()
      observer.disconnect()
    }
  }, [scrollerRef, backdropRef, axis, onDismiss])
}
